import api from "@/lib/api";

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

let loaderPromise = null;

export function loadGoogleMaps() {
  if (!KEY) return Promise.reject(new Error("Google Maps key not configured"));
  if (window.google?.maps?.places) return Promise.resolve(window.google);
  if (!loaderPromise) {
    loaderPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = `https://maps.googleapis.com/maps/api/js?key=${KEY}&libraries=places,geometry`;
      s.async = true;
      s.onload = () => resolve(window.google);
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  return loaderPromise;
}

/** Driving miles through an ordered list of {lat,lng} points, via the Routes API on our backend. */
export async function routeMiles(points) {
  const coords = (points || [])
    .filter((p) => p && p.lat != null && p.lng != null)
    .map((p) => ({ lat: p.lat, lng: p.lng }));
  if (coords.length < 2) return null;
  try {
    const { data } = await api.post("/places/miles", { points: coords });
    return data?.miles ?? null;
  } catch {
    return null;
  }
}

/** Distance, drive time and route line measured on our backend (works on any domain). */
export async function routeInfo(points) {
  const coords = (points || [])
    .filter((p) => p && p.lat != null && p.lng != null)
    .map((p) => ({ lat: p.lat, lng: p.lng }));
  if (coords.length < 2) return null;
  try {
    const { data } = await api.post("/places/route", { points: coords });
    return data?.miles != null ? data : null;
  } catch {
    return null;
  }
}

/** Coordinates for an address the office typed without picking a suggestion. */
export async function geocodeAddress(address) {
  if (!address || address.trim().length < 4) return null;
  try {
    const { data } = await api.post("/places/geocode", { address });
    return data?.lat != null ? data : null;
  } catch {
    return null;
  }
}

/** Server-rendered route map image — no browser key, so no referer restrictions. */
export function staticMapUrl(points, size = "640x320") {
  const coords = (points || []).filter((p) => p && p.lat != null && p.lng != null);
  if (coords.length < 2) return "";
  const list = coords.map((p) => `${p.lat},${p.lng}`).join("|");
  const base = process.env.REACT_APP_BACKEND_URL;
  return `${base}/api/places/staticmap?size=${size}&points=${encodeURIComponent(list)}`;
}

/** The passenger's own position, asked for once and remembered for the session. */
let locationPromise = null;
export function deviceLocation() {
  if (locationPromise) return locationPromise;
  const cached = sessionStorage.getItem("device_location");
  if (cached) {
    locationPromise = Promise.resolve(JSON.parse(cached));
    return locationPromise;
  }
  locationPromise = new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const at = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        sessionStorage.setItem("device_location", JSON.stringify(at));
        resolve(at);
      },
      () => resolve(null),
      { timeout: 8000, maximumAge: 600000 },
    );
  });
  return locationPromise;
}

/** A fresh GPS fix for "use my current location", with a message the passenger can act on. */
export function currentPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("This device cannot share its location. Type the address instead."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const at = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        sessionStorage.setItem("device_location", JSON.stringify(at));
        locationPromise = Promise.resolve(at);
        resolve(at);
      },
      (err) => {
        const messages = {
          1: "Location is blocked for this site. Allow it in your browser or phone settings, then tap again.",
          2: "Your location is unavailable right now. Type the address instead.",
          3: "Could not get a location fix. Try again near a window or with Wi-Fi on.",
        };
        reject(new Error(messages[err?.code] || "Could not read your location."));
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 },
    );
  });
}

/** The street address at a set of coordinates, resolved on our backend. */
export async function addressAt({ lat, lng }) {
  const { data } = await api.post("/places/reverse", { lat, lng });
  return data;
}

/** Address typeahead through our backend (Places API New), biased to where the passenger is. */
export async function placeSuggestions(input, sessionToken, bias = {}) {
  const at = bias.lat != null ? bias : (await deviceLocation()) || {};
  const { data } = await api.post("/places/autocomplete", {
    input,
    session_token: sessionToken,
    lat: at.lat ?? null,
    lng: at.lng ?? null,
    company_id: bias.companyId || null,
  });
  return data?.suggestions || [];
}

/** Resolve a place_id into an address with coordinates. */
export async function placeDetails(placeId) {
  const { data } = await api.get(`/places/details/${placeId}`);
  return data;
}
